import { z } from 'zod';
import { dateTimeSchema, sfaaClassExperienceImportStatusSchema, SfaaClassExperienceImportStatus } from '../../enums.mjs';
import { userId, sfaaClassExperienceImportId, sfaaClassExperienceId } from '../../types/opaque.mjs';
import { currencySchema } from '../../types/utils.mjs';
import { emptyToUndefined } from '../../utils/empty-to-undefined.util.mjs';

const DEFAULT_ROW_LIMIT = 100;
const MAX_ROW_LIMIT = 500;
const rowSortFieldDef = [
    "line",
    "subline",
    "location",
    "sfaaCode",
    "year",
    "writtenPremium",
    "earnedPremium",
    "incurredLoss",
    "lossRatio",
];
const rowSortFieldSchema = z.enum(rowSortFieldDef);
const rowSortDirectionDef = ["asc", "desc"];
const rowSortDirectionSchema = z.enum(rowSortDirectionDef);
const repeatedQueryParam = (item) => z.preprocess((value) => {
    if (value === undefined || value === "")
        return undefined;
    return Array.isArray(value) ? value : [value];
}, z.array(item).optional());
var Import;
(function (Import) {
    let Response;
    (function (Response) {
        Response.schema = z.object({
            id: sfaaClassExperienceImportId,
            uploadedByUserId: userId,
            originalFilename: z.string(),
            status: sfaaClassExperienceImportStatusSchema,
            rowCount: z.number().int().nullable(),
            attributedRowCount: z.number().int(),
            errorMessage: z.string().nullable(),
            errorDetails: z.record(z.string(), z.unknown()).nullable(),
            isStale: z.boolean(),
            createdAt: dateTimeSchema,
            updatedAt: dateTimeSchema,
        });
    })(Response = Import.Response || (Import.Response = {}));
    (function (Accepted) {
        Accepted.schema = Response.schema;
    })(Import.Accepted || (Import.Accepted = {}));
})(Import || (Import = {}));
const isImportInFlight = (status) => status === SfaaClassExperienceImportStatus.Pending ||
    status === SfaaClassExperienceImportStatus.Processing;
var Row;
(function (Row) {
    (function (Response) {
        Response.schema = z.object({
            id: sfaaClassExperienceId,
            line: z.string(),
            subline: z.string(),
            location: z.string(),
            sfaaCode: z.string(),
            year: z.number().int(),
            writtenPremium: currencySchema,
            earnedPremium: currencySchema,
            incurredLoss: currencySchema,
            lossRatio: z.number().nullable(),
        });
    })(Row.Response || (Row.Response = {}));
})(Row || (Row = {}));
var List;
(function (List) {
    (function (Request) {
        Request.schema = z.object({
            lines: repeatedQueryParam(z.string()),
            sublines: repeatedQueryParam(z.string()),
            locations: repeatedQueryParam(z.string()),
            sfaaCodes: repeatedQueryParam(z.string()),
            years: repeatedQueryParam(z.coerce.number().int()),
            minYear: z.preprocess(emptyToUndefined, z.coerce.number().int().optional()),
            maxYear: z.preprocess(emptyToUndefined, z.coerce.number().int().optional()),
            search: z.preprocess(emptyToUndefined, z.string().trim().min(1).optional()),
            sortField: z.preprocess(emptyToUndefined, rowSortFieldSchema.default("year")),
            sortDirection: z.preprocess(emptyToUndefined, rowSortDirectionSchema.default("desc")),
            limit: z.preprocess(emptyToUndefined, z.coerce.number().int().min(1).max(MAX_ROW_LIMIT).default(DEFAULT_ROW_LIMIT)),
            offset: z.preprocess(emptyToUndefined, z.coerce.number().int().min(0).default(0)),
        });
    })(List.Request || (List.Request = {}));
    (function (Response) {
        Response.schema = z.object({
            rows: z.array(Row.Response.schema),
            totalCount: z.number().int(),
            limit: z.number().int(),
            offset: z.number().int(),
            hasMore: z.boolean(),
        });
    })(List.Response || (List.Response = {}));
})(List || (List = {}));
var Filters;
(function (Filters) {
    Filters.codeClassSchema = z.object({
        sfaaCode: z.string(),
        subline: z.string(),
    });
    (function (Response) {
        Response.schema = z.object({
            lines: z.array(z.string()),
            sublines: z.array(z.string()),
            locations: z.array(z.string()),
            sfaaCodes: z.array(z.string()),
            codeClasses: z.array(Filters.codeClassSchema),
            years: z.array(z.number().int()),
            rowCount: z.number().int(),
        });
    })(Filters.Response || (Filters.Response = {}));
})(Filters || (Filters = {}));
const US_TOTAL_LOCATION = "U.S. TOTAL";
const TRAILING_WINDOW_YEARS = 3;
const codeSeriesOf = (sfaaCode) => `${sfaaCode.charAt(0).toUpperCase()}XX`;
const codeSeriesSchema = z.string().regex(/^[0-9A-Za-z]XX$/);
var Series;
(function (Series) {
    (function (Request) {
        Request.schema = z.object({
            line: z.preprocess(emptyToUndefined, z.string().optional()),
            subline: z.preprocess(emptyToUndefined, z.string().optional()),
            codeSeries: z.preprocess(emptyToUndefined, codeSeriesSchema.optional()),
            sfaaCode: z.preprocess(emptyToUndefined, z.string().optional()),
            location: z.preprocess(emptyToUndefined, z.string().default(US_TOTAL_LOCATION)),
            comparison: z.preprocess(emptyToUndefined, z.string().default(US_TOTAL_LOCATION)),
        });
    })(Series.Request || (Series.Request = {}));
    Series.pointSchema = z.object({
        year: z.number().int(),
        writtenPremium: currencySchema,
        earnedPremium: currencySchema,
        incurredLoss: currencySchema,
        lossRatio: z.number().nullable(),
        trailingLossRatio: z.number().nullable(),
    });
    Series.statSchema = z.object({
        average: z.number().nullable(),
        standardDeviation: z.number().nullable(),
        observations: z.number().int(),
    });
    Series.statsSchema = z.object({
        writtenPremium: Series.statSchema,
        incurredLoss: Series.statSchema,
        lossRatio: Series.statSchema,
        trailingLossRatio: Series.statSchema,
    });
    Series.entitySchema = z.object({
        label: z.string(),
        points: z.array(Series.pointSchema),
        stats: Series.statsSchema,
    });
    (function (Response) {
        Response.schema = z.object({
            selected: Series.entitySchema,
            comparison: Series.entitySchema.nullable(),
            years: z.array(z.number().int()),
            availableSfaaCodes: z.array(z.string()),
        });
    })(Series.Response || (Series.Response = {}));
})(Series || (Series = {}));

export { DEFAULT_ROW_LIMIT, Filters, Import, List, MAX_ROW_LIMIT, Row, Series, TRAILING_WINDOW_YEARS, US_TOTAL_LOCATION, codeSeriesOf, codeSeriesSchema, isImportInFlight, rowSortDirectionDef, rowSortDirectionSchema, rowSortFieldDef, rowSortFieldSchema };
